import type {ReactElement} from 'react';
import {useCallback, useEffect, useMemo, useRef} from 'react';
import {v4 as uuidv4} from 'uuid';
import {AiPromptBox} from '@Components/ai-prompt-box/ai-prompt-box';
import {PromptBoxState} from '@Components/ai-prompt-box/ai-prompt-box.types';
import {AiPromptBoxContextualMenu} from '@Components/ai-prompt-box/components/ai-prompt-box-footer-actions/ai-prompt-box-contextual-menu';
import type {FooterLeftAction, FooterLeftActionProps} from '@Components/ai-prompt-box/components/ai-prompt-box-footer-left-actions/ai-prompt-box-footer-left-actions.types';
import {AiPromptBoxUploads} from '@Components/ai-prompt-box/components/ai-prompt-box-footer-actions/ai-prompt-box-uploads/ai-prompt-box-uploads';
import {useAiPromptBox} from '@Components/ai-prompt-box/hooks/useAiPromptBox';
import {addItemsToGrid, deleteGridItems} from '@Components/base-grid';
import type {GridItemStorage} from '@Components/base-grid/components/grid-item';
import {GRID_ITEM_TYPE} from '@Components/base-grid/components/grid-item';
import {AiVideoGenerationStatus} from '@Components/poster-editor/components/poster-ai/poster-ai.types';
import {
  addAiVideoGenerationItem,
  markAiVideoGenerationFailed,
  removeAiVideoGenerationItem,
  selectAiCredits,
  selectGeneratingAiVideoCount,
  selectPendingAiVideoRetry,
  setAiCredits,
} from '@Components/poster-editor/poster-editor-reducer';
import type {BuyCreditsResponse} from '@/types/credits.types';
import {hideLoading} from '@Libraries/loading-toast-library';
import {noop} from '@Utils/general.util';
import {isUserPremium} from '@Libraries/user.library';
import type {AiVideoBackendData, GenerateAiVideoParams} from '@Libraries/ai-video.library';
import {AiVideoGenerationError, AiVideoGenerationErrorCode, generateAiVideo, showAiVideoUpsellDialog} from '@Libraries/ai-video.library';
import {
  AI_VIDEO_GENERATION_LOADING_KEY,
  DEFAULT_AI_VIDEO_AUDIO,
  DEFAULT_AI_VIDEO_QUALITY,
  getAiVideoCreditCost,
  getAiVideoSkeletonDimensions,
  getPromptBoxOptions,
  MAX_CONCURRENT_AI_VIDEO_GENERATIONS,
} from '@Panels/ai-video-panel/ai-video-panel.library';
import {AI_VIDEO_PROMPT_BOX_ID, AiVideoAspectRatio, AiVideoAudio, AiVideoCameraAngle, AiVideoFilterKey, AiVideoQuality} from '@Panels/ai-video-panel/ai-video-panel.types';
import {AI_VIDEO_LOADING_SKELETON_ID_PREFIX, GENERATED_VIDEO_GRID_ID} from '@Panels/ai-video-panel/components/generated-video-grid/generated-video-grid.types';
import {buildSkeletonGridItem} from '@Components/lazy-masonry-grid/generated-media-grid-library';
import {GeneratedVideoItem} from '@Panels/ai-video-panel/components/generated-video-item/generated-video-item';
import type {UserImageAttachment} from '@Components/ai-prompt-box/components/ai-prompt-box-attachments/ai-prompt-box-attachments.types';
import {PromptAttachmentType} from '@Components/ai-prompt-box/components/ai-prompt-box-attachments/ai-prompt-box-attachments.types';
import {getHighResUrlForImage} from '@Libraries/image-item.library';
import {UserImageSource} from '@Libraries/user-image-library';
import {isMobile, isTablet} from 'react-device-detect';
import {stringToEnum} from '@Utils/string.util';
import {isPremiumPlusUser} from '@Utils/user.util';
import {useAppDispatch, useAppSelector} from '@/hooks';
import {updateAiCredits} from '@Libraries/pmw-stock-media-library';

interface AiVideoPromptBoxProps {
  isFullScreen?: boolean;
  onFullscreenPreview: (video: AiVideoBackendData) => void;
  onVideoGenerated: (videos: AiVideoBackendData[]) => void;
}

export function AiVideoPromptBox({isFullScreen = false, onFullscreenPreview, onVideoGenerated}: AiVideoPromptBoxProps): ReactElement {
  const dispatch = useAppDispatch();
  const onFullscreenPreviewRef = useRef(onFullscreenPreview);
  onFullscreenPreviewRef.current = onFullscreenPreview;
  const pendingRetry = useAppSelector(selectPendingAiVideoRetry);
  const remainingCredits = useAppSelector(selectAiCredits);
  const {actions, state, selectedContextualMenuItems} = useAiPromptBox(AI_VIDEO_PROMPT_BOX_ID);
  const generatingCount = useAppSelector(selectGeneratingAiVideoCount);
  const promptBoxStateResetTimeoutRef = useRef<number | null>(null);
  const hasLimitReached = generatingCount >= MAX_CONCURRENT_AI_VIDEO_GENERATIONS;

  const qualityKey: string = AiVideoFilterKey.QUALITY;
  const audioKey: string = AiVideoFilterKey.AUDIO;
  const aspectRatioKey: string = AiVideoFilterKey.ASPECT_RATIO;

  const getSelectedQuality = (): AiVideoQuality => {
    return stringToEnum(selectedContextualMenuItems[qualityKey]?.[0], Object.values(AiVideoQuality), DEFAULT_AI_VIDEO_QUALITY);
  };

  const getSelectedAudio = (): AiVideoAudio => {
    return stringToEnum(selectedContextualMenuItems[audioKey]?.[0], Object.values(AiVideoAudio), DEFAULT_AI_VIDEO_AUDIO);
  };

  const getSelectedAspectRatio = (): AiVideoAspectRatio => {
    return stringToEnum(selectedContextualMenuItems[aspectRatioKey]?.[0], Object.values(AiVideoAspectRatio), AiVideoAspectRatio.PORTRAIT);
  };

  const getCurrentCreditCost = (): number => {
    return getAiVideoCreditCost(getSelectedQuality(), getSelectedAudio());
  };

  useEffect(() => {
    if (!remainingCredits) {
      updateAiCredits();
    }
    actions.update({promptBoxState: PromptBoxState.ACTIVE});
    return (): void => {
      if (promptBoxStateResetTimeoutRef.current !== null) {
        window.clearTimeout(promptBoxStateResetTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (remainingCredits === null || remainingCredits === state.creditsPill?.remainingCredits) {
      return;
    }
    actions.update({creditsPill: {remainingCredits}});
  }, [remainingCredits]);

  useEffect(() => {
    const pillVisibility = [PromptBoxState.ACTIVE, PromptBoxState.LOADING].includes(state.promptBoxState);
    if (pillVisibility !== state.creditsPill?.visible) {
      actions.update({
        creditsPill: {
          visible: pillVisibility,
        },
      });
    }
  }, [state.promptBoxState]);

  useEffect(() => {
    const newCost = getCurrentCreditCost();
    if (newCost !== state.creditsPill?.actionCost) {
      actions.update({
        creditsPill: {
          actionCost: newCost,
        },
      });
    }
  }, [selectedContextualMenuItems[qualityKey], selectedContextualMenuItems[audioKey]]);

  useEffect(() => {
    const message = hasLimitReached ? window.i18next.t('pmwjs_ai_video_limit_reached_try_again_soon') : '';
    actions.update({
      isDailyLimitReached: hasLimitReached,
      showDailyLimitError: false,
      dailyLimitErrorMessage: message.length > 0 ? message : undefined,
    });
  }, [hasLimitReached]);

  const getContextualMenu: FooterLeftAction = useCallback((footerProps): ReactElement => {
    return <AiPromptBoxContextualMenu {...footerProps} maxContextualMenuWidth={310} leftOffset={-49} renderTooltip={true} borderlessSelector={true} />;
  }, []);

  const cameraAngleKey: string = AiVideoFilterKey.CAMERA_ANGLE;

  const getSelectedCameraAngle = (): AiVideoCameraAngle => {
    return stringToEnum(selectedContextualMenuItems[cameraAngleKey]?.[0], Object.values(AiVideoCameraAngle), AiVideoCameraAngle.DEFAULT);
  };

  const getReferenceImageUrls = (): string[] => {
    const imageItems = (state.attachments?.items ?? []).filter(
      (item): item is UserImageAttachment => item.type === PromptAttachmentType.IMAGE || item.type === PromptAttachmentType.CAPTURE_PHOTO
    );
    return imageItems
      .filter((image) => Boolean(image.hashedFilename) && Boolean(image.extension))
      .map((image) => getHighResUrlForImage(image.hashedFilename, image.extension, UserImageSource.UPLOAD));
  };

  const promptBoxUploadsOption: FooterLeftAction = useCallback(
    (footerProps: FooterLeftActionProps): ReactElement | null => (
      <AiPromptBoxUploads
        {...footerProps}
        renderAttachmentTooltip={!isMobile && !isTablet}
        defaultTooltipText={window.i18next.t('pmwjs_add_reference_images')}
      />
    ),
    []
  );

  const promptBoxOptions = useMemo(() => [promptBoxUploadsOption, getContextualMenu], [promptBoxUploadsOption, getContextualMenu]);

  const handleInsufficientCreditsError = (): void => {
    window.PMW.util.require(typeof window.PMW.stripeCheckout === 'undefined', 'stripeCheckoutDialog', true, () => {
      window.PMW.stripeCheckout.showStripeCheckoutForCreditPackPurchase(
        (data: BuyCreditsResponse) => {
          if (data.credits > 0) {
            dispatch(setAiCredits(data.credits - (state.creditsPill?.actionCost ?? getCurrentCreditCost())));
            void onGenerate();
          }
        },
        noop,
        isPremiumPlusUser() ? window.i18next.t('pmwjs_purchase_more_credits_for_ai') : window.i18next.t('pmwjs_purchase_more_credits_for_ai_with_upgrade_message'),
        window.i18next.t('pmwjs_youre_out_of_credits')
      );
    });
  };

  const hasEnoughCredits = (): boolean => {
    const remaining = state.creditsPill?.remainingCredits ?? 0;
    const cost = state.creditsPill?.actionCost ?? getCurrentCreditCost();
    return remaining >= cost;
  };

  const onGenerate = async (): Promise<void> => {
    if (!isUserPremium()) {
      await showAiVideoUpsellDialog();
      return;
    }

    const prompt = state.prompt.trim();
    if (!prompt) {
      return;
    }

    if (hasLimitReached) {
      return;
    }

    if (!hasEnoughCredits()) {
      handleInsufficientCreditsError();
      return;
    }

    actions.update({error: null, promptBoxState: PromptBoxState.LOADING});

    if (promptBoxStateResetTimeoutRef.current !== null) {
      window.clearTimeout(promptBoxStateResetTimeoutRef.current);
    }
    promptBoxStateResetTimeoutRef.current = window.setTimeout(() => {
      promptBoxStateResetTimeoutRef.current = null;
      actions.update({promptBoxState: PromptBoxState.ACTIVE});
    }, 1000);

    const referenceImageUrls = getReferenceImageUrls();
    const params: GenerateAiVideoParams = {
      prompt,
      aspectRatio: getSelectedAspectRatio(),
      quality: getSelectedQuality(),
      generateAudio: getSelectedAudio() === AiVideoAudio.YES,
      cameraAngle: getSelectedCameraAngle(),
      imageUrls: referenceImageUrls.length > 0 ? referenceImageUrls : undefined,
    };
    await runGeneration(params);
  };

  const buildGeneratedVideoGridItem = (response: AiVideoBackendData): GridItemStorage => ({
    type: GRID_ITEM_TYPE.CUSTOM,
    id: `${response.id}`,
    width: response.videoProps.width,
    height: response.videoProps.height,
    data: {video: response},
    children: (
      <GeneratedVideoItem
        key={response.id}
        item={response}
        onFullscreenPreview={(): void => {
          onFullscreenPreviewRef.current(response);
        }}
        autoPlayOnMount
      />
    ),
  });

  const removeLoadingSkeleton = (id: string): void => {
    dispatch(deleteGridItems({gridId: GENERATED_VIDEO_GRID_ID, gridSelectionGroupId: GENERATED_VIDEO_GRID_ID, itemIdsToDelete: [id]}));
  };

  const handleGenerationSuccess = (id: string, response: AiVideoBackendData): void => {
    dispatch(removeAiVideoGenerationItem(id));
    removeLoadingSkeleton(id);
    const remainingLoadingCount = selectGeneratingAiVideoCount(window.PMW.redux.store.getState());
    dispatch(addItemsToGrid({id: GENERATED_VIDEO_GRID_ID, items: [buildGeneratedVideoGridItem(response)], index: remainingLoadingCount}));
    onVideoGenerated([response]);
  };

  const handleGenerationError = (id: string, e: unknown): void => {
    removeLoadingSkeleton(id);
    const errorCode = e instanceof AiVideoGenerationError ? e.code : undefined;
    if (errorCode === AiVideoGenerationErrorCode.INSUFFICIENT_CREDITS) {
      dispatch(removeAiVideoGenerationItem(id));
      handleInsufficientCreditsError();
      return;
    }
    dispatch(markAiVideoGenerationFailed({id, errorCode}));
    console.error(e);
  };

  const hideLoadingToastIfDone = (): void => {
    if (selectGeneratingAiVideoCount(window.PMW.redux.store.getState()) === 0) {
      hideLoading(AI_VIDEO_GENERATION_LOADING_KEY);
    }
  };

  const runGeneration = async (params: GenerateAiVideoParams): Promise<void> => {
    const id = `${AI_VIDEO_LOADING_SKELETON_ID_PREFIX}${uuidv4()}`;
    const {width, height} = getAiVideoSkeletonDimensions(params.aspectRatio as AiVideoAspectRatio);

    dispatch(addAiVideoGenerationItem({id, status: AiVideoGenerationStatus.GENERATING, params}));
    dispatch(addItemsToGrid({id: GENERATED_VIDEO_GRID_ID, items: [buildSkeletonGridItem({id, width, height})], index: 0}));
    dispatch(setAiCredits(remainingCredits - (state.creditsPill?.actionCost ?? getCurrentCreditCost())));

    try {
      const response = await generateAiVideo(params);
      handleGenerationSuccess(id, response);
    } catch (e) {
      handleGenerationError(id, e);
    }
    updateAiCredits();
    hideLoadingToastIfDone();
  };

  useEffect(() => {
    if (!pendingRetry) {
      return;
    }
    dispatch(removeAiVideoGenerationItem(pendingRetry.id));
    void runGeneration(pendingRetry.params);
  }, [pendingRetry, dispatch]);

  return (
    <AiPromptBox
      id={AI_VIDEO_PROMPT_BOX_ID}
      containerClasses={`${hasLimitReached && !(isFullScreen && !isMobile) ? 'spacing-p-b-3' : 'spacing-p-b-6'} ${hasLimitReached && isFullScreen && !isMobile ? 'spacing-p-t-6' : ''}`}
      initialConfig={getPromptBoxOptions(remainingCredits ?? 0)}
      onGenerate={onGenerate}
      onRegenerate={onGenerate}
      options={promptBoxOptions}
      focus={true}
      allowSoftSendDuringUploads={false}
    />
  );
}
